<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Mobile overlay for sidebar -->
    <div
      v-if="isSidebarOpen && isMobile"
      class="fixed inset-0 z-40 bg-black bg-opacity-50 dark:bg-opacity-70 lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <UnifiedSidebar
      :is-open="isSidebarOpen"
      :is-mobile="isMobile"
      @toggle="toggleSidebar"
      @close="closeSidebar"
    />

    <!-- Main content -->
    <div
      class="transition-all duration-300 ease-in-out min-h-screen"
      :class="mainContentClasses"
    >
      <!-- Top navigation -->
      <TopNav 
        @toggle-sidebar="toggleSidebar"
        :is-sidebar-open="isSidebarOpen"
      />

      <!-- Page content -->
      <main class="p-3 sm:p-4 md:p-6 lg:p-8">
        <div class="max-w-12xl mx-auto">
          <!-- Breadcrumbs (if route has breadcrumbs meta) -->
          <Breadcrumbs v-if="showBreadcrumbs" :items="breadcrumbItems" class="mb-6" />
          
          <!-- Page transition -->
          <Transition
            name="page"
            mode="out-in"
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <router-view />
          </Transition>
        </div>
      </main>
    </div>

    <!-- Command palette (Ctrl+K) -->
    <CommandPalette v-if="showCommandPalette" @close="closeCommandPalette" />
    
    <!-- Global notifications -->
    <NotificationCenter v-if="hasNotifications" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

// Components
import UnifiedSidebar from '@/components/layout/UnifiedSidebar.vue'
import TopNav from '@/components/layout/TopNav.vue'
import CommandPalette from '@/components/ui/CommandPalette.vue'
import NotificationCenter from '@/components/ui/NotificationCenter.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'

const route = useRoute()
const appStore = useAppStore()
const authStore = useAuthStore()

// State
const showCommandPalette = ref(false)
const isMobile = ref(false)

// Computed properties
const isSidebarOpen = computed(() => appStore.isSidebarOpen)

const mainContentClasses = computed(() => ({
  'lg:ml-64': isSidebarOpen.value && !isMobile.value,
  'lg:ml-20': !isSidebarOpen.value && !isMobile.value,
  'ml-0': isMobile.value
}))

const showBreadcrumbs = computed(() => {
  return route.meta?.breadcrumbs?.length > 0
})

const breadcrumbItems = computed(() => {
  return route.meta?.breadcrumbs || []
})

const hasNotifications = computed(() => {
  try {
    const notificationsArray = Array.isArray(appStore.notifications)
      ? appStore.notifications
      : []
    const validNotifications = notificationsArray.filter(n =>
      n && n.id && (n.title || n.message)
    )
    return validNotifications.length > 0
  } catch (error) {
    console.warn('Error checking notifications:', error)
    return false
  }
})

// Methods
const toggleSidebar = () => {
  appStore.toggleSidebar()
}

const closeSidebar = () => {
  appStore.closeSidebar()
}

const closeCommandPalette = () => {
  showCommandPalette.value = false
}

const updateMobileStatus = () => {
  isMobile.value = window.innerWidth < 1024
  
  // Auto-close sidebar on mobile when route changes
  if (isMobile.value && isSidebarOpen.value) {
    closeSidebar()
  }
}

// Keyboard shortcuts
const handleKeydown = (event) => {
  // Command palette (Ctrl+K or Cmd+K)
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    showCommandPalette.value = true
  }

  // Toggle sidebar (Ctrl+B or Cmd+B)
  if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
    event.preventDefault()
    toggleSidebar()
  }

  // Close modals with Escape
  if (event.key === 'Escape') {
    if (showCommandPalette.value) {
      closeCommandPalette()
    }
  }
}

// Lifecycle
onMounted(() => {
  // Set up event listeners
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMobileStatus)
  
  // Initial mobile status check
  updateMobileStatus()
  
  // Fetch initial dashboard stats
  appStore.fetchDashboardStats()
  
  // Clear any invalid notifications and fetch fresh ones if user is authenticated
  if (authStore.isAuthenticated) {
    // Clear any existing invalid notifications
    const validNotifications = (appStore.notifications || []).filter(n =>
      n && n.id && (n.title || n.message)
    )
    if (validNotifications.length !== appStore.notifications.length) {
      appStore.clearAllNotifications()
    }

    // Temporarily disabled - notifications system not fully implemented
    // appStore.fetchNotifications()
  }
  
  // Add debug methods to window for development
  if (import.meta.env.DEV) {
    window.appStore = appStore
    window.debugNotifications = {
      clear: () => appStore.clearAllNotifications(),
      addTest: (type) => appStore.addTestNotification(type),
      addSuccess: () => appStore.addTestNotification('success'),
      addWarning: () => appStore.addTestNotification('warning'),
      addError: () => appStore.addTestNotification('error'),
      addInfo: () => appStore.addTestNotification('info')
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMobileStatus)
})

// Watch for route changes to close sidebar on mobile
watch(route, (newRoute) => {
  try {
    if (isMobile.value && isSidebarOpen.value) {
      closeSidebar()
    }
  } catch (error) {
    console.warn('Error handling route change:', error)
  }
}, { flush: 'post' })
</script>

<style scoped>
/* Ensure smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Page transition styles */
.page-enter-active,
.page-leave-active {
  transition: all 0.2s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>